import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchAssignmentAPI,
  fetchParagraphListAPI,
  fetchSectionListAPI,
  updateSectionAPI,
  retrieveAssignmentAPI,
  createRetryAssignmentAPI,
  fetchRetryAssignmentAPI,
  fetchRetryAssignmentsByTitleAPI,
} from "./assignmentAPI";
import { fetchStudentPaperAssignmentsAPI } from "../paper-assignment/paperAssignmentAPI";

export const fetchAssignmentList = createAsyncThunk(
  "assignment/fetchAssignmentList",
  async (
    { studentId, isFinished = false, page = 1, limit = 11 },
    { rejectWithValue }
  ) => {
    try {
      // 기존 과제 가져오기
      const assignmentResponse = await fetchAssignmentAPI(
        studentId,
        isFinished,
        page,
        limit
      );

      // 지필 과제 가져오기
      let paperAssignments = [];
      try {
        const paperResponse = await fetchStudentPaperAssignmentsAPI(studentId);
        
        // 지필 과제를 기존 과제 형태로 변환
        const homeworkAssignments = paperResponse.map(paperAssignment => {
          // 각 homework_file을 별도의 과제로 변환
          return paperAssignment.homework_files.map(homework => {
            const isCompleted = homework.student_submission !== null;
            const matchesFinishedFilter = isFinished ? isCompleted : !isCompleted;
            
            if (!matchesFinishedFilter) return null;

            return {
              id: `paper_${homework.id}`,
              type: 'paper_assignment',
              title: `${paperAssignment.level_name} - ${homework.title}`,
              created_at: paperAssignment.created_at,
              is_finished: isCompleted,
              score: homework.student_submission?.score || null,
              closed_at: homework.end_date || null,
              progress_rate: isCompleted ? 100 : 0,

              paper_assignment_data: {
                assignment_id: paperAssignment.id,
                homework_id: homework.id,
                homework: homework,
                level_name: paperAssignment.level_name,
                attendance: paperAssignment.attendance,
                attitude: paperAssignment.attitude,
                comment: paperAssignment.comment,
                class_content: paperAssignment.class_content || [],
                student_submission: homework.student_submission
              }
            };
          }).filter(Boolean);
        }).flat();

        // 수업 내용(class_content)를 별도 과제로 변환
        const classContentAssignments = paperResponse.map(paperAssignment => {
          let classContent = paperAssignment.class_content;
          
          // class_content가 문자열인 경우 JSON 파싱
          if (typeof classContent === 'string') {
            try {
              classContent = JSON.parse(classContent);
            } catch (error) {
              console.warn('Failed to parse class_content:', error);
              classContent = [];
            }
          }
          
          if (!classContent || !Array.isArray(classContent) || classContent.length === 0) {
            return [];
          }

          return classContent.map((content, index) => {
            // 시험이나 과제만 완료 과제로 표시 (수업은 제외)
            if (content.type === 'lesson') return null;
            
            // 완료 과제 탭에서만 표시 (isFinished가 true일 때만)
            if (!isFinished) return null;

            const typeEmoji = content.type === 'exam' ? '📋' : '📝';
            const typeName = content.type === 'exam' ? '시험' : '과제';

            return {
              id: `class_content_${paperAssignment.id}_${index}`,
              type: 'class_content',
              title: `${paperAssignment.level_name} - ${typeName}: ${content.title}`,
              created_at: paperAssignment.created_at,
              is_finished: true, // 수업 내용은 항상 완료된 것으로 간주
              score: content.score ? parseInt(content.score) : null,
              totalScore: content.totalScore ? parseInt(content.totalScore) : 100,
              deadline: null,
              progress_rate: 100,
              
              class_content_data: {
                assignment_id: paperAssignment.id,
                content: content,
                level_name: paperAssignment.level_name,
                typeEmoji: typeEmoji,
                typeName: typeName
              }
            };
          }).filter(Boolean);
        }).flat();

        paperAssignments = [...homeworkAssignments, ...classContentAssignments];
      } catch (paperError) {
        console.warn('Failed to fetch paper assignments:', paperError);
      }

      // 두 데이터 합치기
      const combinedResults = [...assignmentResponse.results, ...paperAssignments];
      
      // 출제일 기준으로 정렬 (최신순)
      combinedResults.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      return {
        ...assignmentResponse,
        results: combinedResults
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const retreiveAssignment = createAsyncThunk(
  "assignment/retrieveAssignment",
  async (assignmentId, { rejectWithValue }) => {
    try {
      const response = await retrieveAssignmentAPI(assignmentId);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchParagraphList = createAsyncThunk(
  "assignment/fetchParagraphList",
  async (assignmentId, { rejectWithValue }) => {
    try {
      const response = await fetchParagraphListAPI(assignmentId);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchSectionList = createAsyncThunk(
  "assignment/fetchSectionList",
  async (paragraphId, { rejectWithValue }) => {
    try {
      const response = await fetchSectionListAPI(paragraphId);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateSection = createAsyncThunk(
  "assignment/updateSection",
  async ({ sectionId, data }, { rejectWithValue }) => {
    try {
      const response = await updateSectionAPI(sectionId, data);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createRetryAssignment = createAsyncThunk(
  "assignment/createRetryAssignment",
  async ({ assignmentId, maxRetries = 3 }, { rejectWithValue }) => {
    try {
      const response = await createRetryAssignmentAPI(assignmentId, maxRetries);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const fetchRetryAssignmentList = createAsyncThunk(
  "assignment/fetchRetryAssignmentList",
  async ({ studentId, page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const response = await fetchRetryAssignmentAPI(studentId, page, limit);

      // If no retry assignments found with the primary method, try the fallback
      if (response.results.length === 0 && page === 1) {
        console.log("Primary method returned no results, trying fallback...");
        const fallbackResponse = await fetchRetryAssignmentsByTitleAPI(
          studentId,
          page,
          limit
        );
        return fallbackResponse;
      }

      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const assignmentSlice = createSlice({
  name: "assignment",
  initialState: {
    current_page: 1,
    total_pages: 1,
    assignmentList: [],
    retryAssignmentList: [],
    retryCurrentPage: 1,
    retryTotalPages: 1,
    paragraphList: [],
    sectionList: [],
    assignment: null,
    paragraph: null,
    section: null,
    error: null,
    retryError: null,
  },
  reducers: {
    setAssignment(state, action) {
      state.assignment = action.payload;
    },
    setParagraph(state, action) {
      state.paragraph = action.payload;
    },
    setSection(state, action) {
      const section = action.payload;

      // Calculate resume index from answer array
      let resumeIndex = 0;
      if (section.answer && section.answer.length > 0) {
        const foundIndex = section.answer.findIndex(
          page => !page.isSubmitted
        );
        resumeIndex = foundIndex !== -1 ? foundIndex : 0;
      }

      state.section = {
        ...section,
        resumeIndex
      };
    },
    setSectionList(state, action) {
      state.sectionList = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAssignmentList.fulfilled, (state, action) => {
        const { results, current_page, total_pages } = action.payload;
        state.assignmentList = results;
        state.current_page = current_page;
        state.total_pages = total_pages;
        state.error = null;
      })
      .addCase(fetchAssignmentList.rejected, (state, action) => {
        state.assignmentList = [];
        state.error = action.payload;
      })
      .addCase(retreiveAssignment.fulfilled, (state, action) => {
        state.assignment = action.payload;
        state.error = null;
      })
      .addCase(retreiveAssignment.rejected, (state, action) => {
        state.assignment = null;
        state.error = action.payload;
      })
      .addCase(fetchParagraphList.fulfilled, (state, action) => {
        state.paragraphList = action.payload;
        state.error = null;
      })
      .addCase(fetchParagraphList.rejected, (state, action) => {
        state.paragraphList = [];
        state.error = action.payload;
      })
      .addCase(fetchSectionList.fulfilled, (state, action) => {
        state.sectionList = action.payload;
        if (state.assignment.type === "vocabulary") {
          const unknownList = state.sectionList
            .filter((section) => section.type === "study")
            .flatMap((section) =>
              section.answer.filter((word) => word.isUnknown)
            );
          state.sectionList = state.sectionList.filter(
            (section) => section.type !== "unknown"
          );

          if (unknownList.length > 0) {
            const unknownSection = {
              id: 0,
              type: "unknown",
              type_name: "모르는 단어 모음",
              question: {
                words: unknownList,
              },
              answer: unknownList,
              is_finished: false,
            };
            state.sectionList.unshift(unknownSection);
          }
        }
        state.error = null;
      })
      .addCase(fetchSectionList.rejected, (state, action) => {
        state.sectionList = [];
        state.error = action.payload;
      })
      .addCase(updateSection.fulfilled, (state, action) => {
        state.sectionList = state.sectionList.map((section) =>
          section.id === action.payload.id ? action.payload : section
        );
        if (state.assignment.type === "vocabulary") {
          const unknownList = state.sectionList
            .filter((section) => section.type === "study")
            .flatMap((section) =>
              section.answer.filter((word) => word.isUnknown)
            );
          state.sectionList = state.sectionList.filter(
            (section) => section.type !== "unknown"
          );

          let unknownSection = null;
          if (unknownList.length > 0) {
            unknownSection = {
              id: 0,
              type: "unknown",
              type_name: "모르는 단어 모음",
              question: {
                words: unknownList,
              },
              answer: unknownList,
              is_finished: false,
            };
            state.sectionList.unshift(unknownSection);
          }

          if (state.section.type === "unknown") {
            state.section = unknownSection;
          } else {
            state.section = action.payload;
          }
        } else {
          state.section = action.payload;
        }
        if (state.sectionList.every((section) => section.is_finished)) {
          state.paragraphList = state.paragraphList.map((paragraph) =>
            paragraph.id === state.paragraph.id
              ? {
                  ...paragraph,
                  is_finished: true,
                }
              : paragraph
          );
        }
        state.error = null;
      })
      .addCase(updateSection.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(createRetryAssignment.fulfilled, (state, action) => {
        state.retryError = null;
        // Optionally refresh retry assignment list after creation
      })
      .addCase(createRetryAssignment.rejected, (state, action) => {
        state.retryError = action.payload;
      })
      .addCase(fetchRetryAssignmentList.fulfilled, (state, action) => {
        const { results, current_page, total_pages } = action.payload;
        state.retryAssignmentList = results;
        state.retryCurrentPage = current_page;
        state.retryTotalPages = total_pages;
        state.retryError = null;
      })
      .addCase(fetchRetryAssignmentList.rejected, (state, action) => {
        state.retryAssignmentList = [];
        state.retryError = action.payload;
      });
  },
});

export const { setAssignment, setParagraph, setSection, setSectionList } =
  assignmentSlice.actions;
export default assignmentSlice.reducer;
